if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.ifMediaStack = this.hasAttribute("data-media-stack");
        this.lastVariantMediaId = parseInt(
          this.querySelector(".is-current-variant-media").dataset.mediaId,
        ); // 当前选择的产品变体对应的MediaId

        if (!this.thumbnails) return;
        this.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((thumbnail) => {
            thumbnail.addEventListener(
              "click",
              this.onThumbnailClick.bind(this),
            );
          });

        this.addEventListener("sliderChanged", this.onSlideChanged.bind(this));
      }

      get viewer() {
        return this.querySelector('[id^="Gallery-Viewer"]');
      }

      get thumbnails() {
        return this.querySelector('[id^="Gallery-Thumbnail-Slider"]');
      }

      /**
       * 媒体切换事件
       * @param event
       */
      onSlideChanged(event) {
        if (!event || !event.detail) return;

        const mediaId = event.detail.currentElement.dataset.mediaId;

        // 滚动缩略图到当前媒体
        const thumbnail = this.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );
        if (!thumbnail) return;

        if (
          !thumbnail
            .querySelector(".thumbnail-media")
            .hasAttribute("aria-current")
        ) {
          this.setActiveThumbnail(thumbnail);
        }
        if (this.thumbnails.initSliderStatus)
          this.thumbnails.moveElementToOptimalPosition(thumbnail); // 将缩略图元素移动到合适的位置
      }

      /**
       * 点击缩略图处理
       */
      onThumbnailClick(event) {
         const thumbnail = event.currentTarget;
         const mediaId = thumbnail.getAttribute("data-target");

         this.setActiveThumbnail(thumbnail);
         this.changeMedia(mediaId);
         this.syncColorOptionWithMedia(mediaId);
      }

      /**
       * 更新相册，如果需要隐藏其它变体图片，需要重新安装轮播组件
       * @param currentVariantMediaId 当前变体的 Featured Media Id
       * @param {Object} [options]
       * @param {boolean} [options.scrollIntoView=true] On mobile, scroll the
       *   gallery into view after switching. Pass false for switches the
       *   shopper did not trigger, so the page never jumps.
       */
      updateGallery(currentVariantMediaId, { scrollIntoView = true } = {}) {
        if (currentVariantMediaId == null) return;
        this.lastVariantMediaId = currentVariantMediaId;

        // 修改当前变体关联的媒体
        this.viewer
          .querySelectorAll(".is-current-variant-media")
          .forEach((element) =>
            element.classList.remove("is-current-variant-media"),
          );
        this.viewer
          .querySelectorAll(`[data-media-id="${currentVariantMediaId}"]`)
          .forEach((element) =>
            element.classList.add("is-current-variant-media"),
          );

        if (this.thumbnails) {
          this.thumbnails
            .querySelector(".is-current-variant-thumbnail")
            ?.classList.remove("is-current-variant-thumbnail");
          this.thumbnails
            .querySelector(`[data-target="${currentVariantMediaId}"]`)
            ?.classList.add("is-current-variant-thumbnail");
        }

        // 如果开启了隐藏其它变体设置
        if (this.hasAttribute("data-hide-variants")) {
          // 切换当前变体的媒体组
          const groupName = this.viewer.querySelector(
            `[data-media-id="${currentVariantMediaId}"]`,
          )?.dataset.mediaGroup;
          this.viewer
            .querySelectorAll(".is-current-variant-media-group")
            .forEach((element) =>
              element.classList.remove("is-current-variant-media-group"),
            );
          if (groupName) {
            this.viewer
              .querySelectorAll(`[data-media-group='${groupName}']`)
              .forEach((element) => {
                element.classList.add("is-current-variant-media-group");
              });
          }

          if (this.thumbnails) {
            this.thumbnails
              .querySelectorAll(".is-current-variant-thumbnail-group")
              .forEach((element) =>
                element.classList.remove("is-current-variant-thumbnail-group"),
              );
            if (groupName) {
              this.thumbnails
                .querySelectorAll(`[data-thumbnail-group='${groupName}']`)
                .forEach((element) => {
                  element.classList.add("is-current-variant-thumbnail-group");
                });
            }
          }

          // 如果启用了隐藏其它变体媒体，需要重新初始化轮播
          if (!this.ifMediaStack || webvista.isMobileScreen()) {
            this.viewer.reInstall();
            if (this.thumbnails) this.thumbnails.reInstall();
          }
        }

        const mediaId = this.resolveMediaToShow(currentVariantMediaId);
        this.changeMedia(mediaId);

        // 手机端，当媒体切换的时候，返回页面顶部 (skipped when the change came from the gallery)
        if (
          scrollIntoView &&
          mediaId === currentVariantMediaId &&
          webvista.isMobileScreen()
        ) {
          webvista.scrollElementToHeaderBottom(this, 16);
        }
      }

      /**
       * 设置活跃媒体
       * @param mediaId
       */
      changeMedia(mediaId) {
        const activeMedia = this.viewer.querySelector(
          `[data-media-id="${mediaId}"]`,
        );

        if (this.ifMediaStack) {
          // 如果是 Stack 布局，需要将当前变体 Featured Media 放置最前面
          activeMedia.parentElement.prepend(activeMedia);
        } else {
          // 轮播模式，切换当前轮播
          if (this.viewer.initSliderStatus)
            this.viewer.slideByElement(activeMedia);
        }
      }

      /**
       * 设置活跃缩略图
       * @param thumbnail
       */
      setActiveThumbnail(thumbnail) {
        if (!this.thumbnails || !thumbnail) return;

        this.thumbnails
          .querySelectorAll(".thumbnail-media")
          .forEach((element) => element.removeAttribute("aria-current"));

        thumbnail.querySelector("button").setAttribute("aria-current", true);
      }

              /**
         * Media id -> colour option data emitted by main-product.liquid.
         * Parsed once and cached; null when the product has no colour option.
         */
        get variantMediaColors() {
          if (this._variantMediaColors === undefined) {
            const script = this.closest(".product-media-gallery")?.querySelector(
              "script[data-variant-media-map]",
            );
            try {
              this._variantMediaColors = script ? JSON.parse(script.textContent) : null;
            } catch {
              this._variantMediaColors = null;
            }
          }
          return this._variantMediaColors;
        }

        getColorForMedia(mediaId) {
          return this.variantMediaColors?.colors[String(mediaId)] ?? null;
        }

        /**
         * When the shopper picks a gallery image that belongs to another colour,
         * select that colour's swatch. Dispatching "change" lets the variant
         * picker run its normal flow (price, URL, form, gallery).
         */
        syncColorOptionWithMedia(mediaId) {
          const color = this.getColorForMedia(mediaId);
          if (!color) return; // not a variant image (lifestyle shot, etc.)

          const optionGroup = this.closest(".product-container")?.querySelectorAll(
            "high-variant-selects .product-form-input",
          )[this.variantMediaColors.optionIndex];
          const input = [
            ...(optionGroup?.querySelectorAll('input[type="radio"]') ?? []),
          ].find((radio) => radio.value === color);

          if (!input || input.checked) return; // already selected: prevents loops

          this.pinnedMediaId = mediaId; // keep showing the image the shopper clicked
          input.checked = true;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }

        /**
         * Called from updateGallery. If the clicked image belongs to the same
         * colour as the new variant, keep it instead of jumping to the variant's
         * featured image.
         */
        resolveMediaToShow(variantMediaId) {
          const pinned = this.pinnedMediaId;
          this.pinnedMediaId = null;
          const samePinnedColor =
            pinned &&
            this.getColorForMedia(pinned) === this.getColorForMedia(variantMediaId);
          return samePinnedColor ? pinned : variantMediaId;
        }
    },
  );
}
